import { useState, useEffect } from "react";
import { Heading, Box, Text, Center, Tooltip, Flex, useColorMode, useColorModeValue } from "@chakra-ui/react";
import {
  FlexibleWidthXYPlot,
  VerticalBarSeries,
  LineSeries,
  Crosshair,
} from "react-vis";
import moment from "moment";
import PuffLoader from "react-spinners/PuffLoader";
import { QuestionIcon } from "@chakra-ui/icons";
import numbro from "numbro";
import { textStyle } from "theme/names";

export function FollowerGain({
  stats = [],
  isLoading = false,
  previousDayStat = undefined,
  dateSeries = [],
}) {
  const [index, setIndex] = useState(null);
  const [hoveredPoint, setHoveredPoint] = useState<any>();
  const [data, setData] = useState<any>([]);
  const [crosshairValues, setCrosshairValues] = useState([]);
  const { colorMode } = useColorMode()

  useEffect(() => {
    if (stats && stats.length > 0) {
      let newSeries: any = [...dateSeries];
      stats.forEach((s: any) => {
        const formattedDate = moment(s.dateFetched.toDate ? s.dateFetched.toDate() : s.dateFetched).format(
          "MMM. Do ’YY"
        );
        const index = newSeries.findIndex((n) => n.date === formattedDate);

        if (index !== -1) {
          newSeries[index].followerCount = s.followerCount;
        }
      });
      setData(newSeries);
    } else {
      setData([]);
    }
  }, [stats]);

  let newArr: any = data.slice().reverse();

  newArr.forEach((d, i, arr) => {
    d.x = arr.length - i - 1;
    d.y = d.followerCount || arr[(i + arr.length - 1)%arr.length]?.y || 0;
  });
  
  const lastElement = newArr[0]
  const secondLastElement = newArr[1]
  if (!lastElement?.y && secondLastElement) {
    lastElement.y = secondLastElement?.y
  }

  // Line data

  const totalFollowers = newArr.reverse();

  // Bar data
  const followersDiff = data.map((d, i, arr) => {
    return {
      x: i,
      y: !d.followerCount
        ? 0
        : d.followerCount -
            (i === 0
              ? // @ts-ignore
                previousDayStat?.followerCount
                ? // @ts-ignore
                  previousDayStat?.followerCount
                : d.followerCount
              : arr[i - 1].followerCount) || 0.01,
      color: i === index ? "dodgerblue" : colorMode === "dark" ? "darkgray" : "lightgray",
    };
  });

  // @ts-ignore
  const hoveredData = data[index];
  // @ts-ignore
  const beforeHoveredItem = index === 0 ? previousDayStat : data[index - 1];
  const followerDiff =
    hoveredData?.followerCount - beforeHoveredItem?.followerCount;

  const maxYDomainBarSeries =
    Math.max(...followersDiff.map((item) => item.y)) > 0.01
      ? Math.max(...followersDiff.map((item) => item.y))
      : 1;

  return (
    <Box>
      <Flex alignItems="center">
        <Heading fontSize="xl">Daily Follower Gain</Heading>
        <Tooltip
          hasArrow
          label={
            <>
              <Text textStyle={textStyle["body.medium.light"]}>
                Your daily followers change is represented by bars and total
                followers in red.
              </Text>
              <br />
              <Text textStyle={textStyle["body.medium.light"]}>
                Note: Analytics kicks off only after you activate it so there
                are chances you might see some blank spaces in charts because we
                won't have earlier data.
              </Text>
            </>
          }
        >
          <QuestionIcon color="gray.400" mt={1} ml={2} />
        </Tooltip>
      </Flex>
      {!isLoading && stats.length <= 0 && (
        <Text
          textStyle={textStyle["body.medium.light"]}
          p="8"
          textAlign="center"
        >
          No chart data found. Please try a different timeframe
        </Text>
      )}
      {isLoading && (
        <Center pt={10}>
          <PuffLoader color={"#0A66C2"} loading={isLoading}
            cssOverride={{
              display: "block",
              margin: "0 auto",
              borderColor: "red",
            }} size={50}
          />
        </Center>
      )}
      {!isLoading && (
        <Box
          position="relative"
          h="300px"
          maxW="100%"
          ml={{ base: "0", md: "4" }}
          mt={{ base: "4", md: "8" }}
        >
          <Box position="absolute" maxW="100%" w="100%" mt="8">
            <FlexibleWidthXYPlot
              height={300}
              xType="ordinal"
              onMouseLeave={() => {
                setIndex(null);
                setCrosshairValues([]);
              }}
            >
              <VerticalBarSeries
                yDomain={[
                  Math.min(...followersDiff.map((item) => item.y)),
                  maxYDomainBarSeries,
                ]}
                data={followersDiff}
                color="#15b7f7"
                colorType="literal"
              />
            </FlexibleWidthXYPlot>
          </Box>
          <Box position="absolute" maxW="100%" w="100%" mt="8" zIndex="1">
            <FlexibleWidthXYPlot
              // yDomain={[
              //   20000,
              //   22000
              // ]}
              yDomain={[
                Math.min(...totalFollowers.map((item) => item.y)) -
                  Math.min(...totalFollowers.map((item) => item.y)) * 0.01,
                Math.max(...totalFollowers.map((item) => item.y)) +
                  Math.max(...totalFollowers.map((item) => item.y)) * 0.01,
              ]}
              height={300}
              xType="ordinal"
              onMouseLeave={() => {
                setIndex(null);
                setCrosshairValues([]);
              }}
            >
              <LineSeries
                data={totalFollowers}
                curve="curveMonotoneX"
                color="red"
                onNearestX={(d, { index: xIndex }) => {
                  setIndex(xIndex);
                  if (d.y !== 0) {
                    // @ts-ignore
                    setCrosshairValues([followersDiff[xIndex]]);
                  } else {
                    setCrosshairValues([]);
                  }
                }}
                onValueMouseOver={(value) => {
                  setHoveredPoint(value);
                }}
                onValueMouseOut={() => setHoveredPoint(null)}
              />
              <Crosshair values={crosshairValues}>
                <Box
                  background={useColorModeValue("white", "gray.600")}
                  padding="4"
                  boxShadow="dark-lg"
                  color={useColorModeValue("#1E1E1E", "white")}
                  borderRadius="xl"
                  textAlign="center"
                  width="28"
                >
                  {hoveredData?.followerCount ? (
                    <>
                      {followerDiff ? (
                        <Text
                          textStyle={textStyle["body.medium.light"]}
                          fontWeight="bold"
                          fontSize="xs"
                        >
                          {followerDiff > 0 && "+"}
                          {numbro(followerDiff)?.format({
                            thousandSeparated: true,
                          })}{" "}
                          followers
                        </Text>
                      ) : null}

                      <Text
                        textStyle={textStyle["body.medium.light"]}
                        fontSize="11px"
                        my="0.5"
                      >
                        Total:{" "}
                        {numbro(hoveredData?.followerCount)?.format({
                          thousandSeparated: true,
                        })}
                      </Text>
                    </>
                  ) : (
                    <>
                      <Text
                        textStyle={textStyle["body.bold.light"]}
                        fontWeight="bold"
                        fontSize="xs"
                      >
                        No Data Available!
                      </Text>
                      <Text
                        textStyle={textStyle["body.medium.light"]}
                        mt="1"
                        fontSize="11px"
                        mb="1"
                      >
                        {hoveredData?.date === moment()?.format("MMM. Do ’YY")
                          ? "Analytics hasn't analyzed today's data yet"
                          : "Analytics kicks off only after the date you activate it"}
                      </Text>
                    </>
                  )}

                  <Text
                    textStyle={textStyle["body.medium.light"]}
                    fontSize="11px"
                  >
                    {hoveredData?.date}
                  </Text>
                </Box>
              </Crosshair>
            </FlexibleWidthXYPlot>
          </Box>
        </Box>
      )}
    </Box>
  );
}
